import moment from 'moment-timezone';
import { useState } from 'react';
import { useMutation } from 'react-query';

import { LifeConstants } from '@tymbe/schema/constants/life_constants';
import { ShiftType } from '@tymbe/schema/enums';

import feathersClient from '../../../../../apiClient';
import { ErrorAlert } from '../../../../../components/alerts';
import { SecondaryButton } from '../../../../../components/buttons';
import AlertModal from '../../../../../components/modals/AlertModal';
import { RequestBody } from '../../../../../types/ReactQuery';
import { ApplicationData, ApplicationState } from '../../../../../types/TymbeApi';
import { DeepPartial } from '../../../../../types/utils';

export interface EditApplicationFormData {
  state:{ label: string, value: ApplicationState } | null;
}

export interface EditApplicationStateProps {
  applicationId: number;
  manShiftId: number;
  onSuccess?: () => void;
}

// Returns true if the BE would actually refund a life given the current state of
// the application + tymber. The dialog is only shown when this returns true; on
// false we go straight to SYSTEM_CANCELED with no life-grant decision to make.
const isEligibleForLifeRefund = async (applicationId: number): Promise<boolean> => {
  try {
    const application = await feathersClient
      .service('application')
      .get(applicationId, { query: { $eager: '[shift]' } });

    if (application?.state !== ApplicationState.CONFIRMED) return false;
    if (!application.shift || application.shift.type === ShiftType.VACATION) return false;

    const hoursUntilShift = moment(application.shift.start_time)
      .tz('Europe/Prague')
      .diff(moment().tz('Europe/Prague'), 'hours');
    // `<= 0` rejects past shifts — they would otherwise satisfy the upper bound.
    if (hoursUntilShift <= 0) return false;
    if (hoursUntilShift > LifeConstants.employerCancellationMaxHoursBeforeShift) return false;

    const personData = await feathersClient
      .service('person-data')
      .get(application.person_id);
    if (!personData || personData.lives >= LifeConstants.maxLives) return false;

    return true;
  } catch {
    return false;
  }
};

const EditApplicationState = ({ applicationId, manShiftId, onSuccess }: EditApplicationStateProps) => {
  const [showLifeRefundModal, setShowLifeRefundModal] = useState(false);
  const [showFreeUpManShiftModal, setShowFreeUpManShiftModal] = useState(false);
  // When the admin confirms the refund in the first modal we carry that
  // decision through to the second (Nahradit) modal — its onYes/onNo actually
  // fires the patch with the resolved state.
  const [pendingState, setPendingState] = useState<ApplicationState>(ApplicationState.SYSTEM_CANCELED);

  const { mutateAsync: patchApplication } = useMutation(
    ['patchApplication', applicationId],
    async (req: RequestBody<DeepPartial<ApplicationData>>): Promise<void> => {
      await feathersClient.service('application').patch(req.id, req.body);
    },
    {
      onSuccess,
    },
  );

  const startCancelFlow = async () => {
    const eligible = await isEligibleForLifeRefund(applicationId);
    setPendingState(ApplicationState.SYSTEM_CANCELED);
    if (eligible) {
      setShowLifeRefundModal(true);
    } else {
      setShowFreeUpManShiftModal(true);
    }
  };

  const onLifeRefundChoice = (grant: boolean) => {
    setPendingState(grant ? ApplicationState.EMPLOYER_CANCELED : ApplicationState.SYSTEM_CANCELED);
    setShowLifeRefundModal(false);
    setShowFreeUpManShiftModal(true);
  };

  const cancelApplication = async () => {
    try {
      await patchApplication({ id: applicationId, body: { state: pendingState } });
    } catch {
      ErrorAlert('Při úpravě přihlášky došlo k chybě');
    }

    setShowFreeUpManShiftModal(false);
  };

  const cancelApplicationAndRemoveManshift = async () => {
    try {
      await patchApplication({ id: applicationId, body: { state: pendingState } });
      await feathersClient.service('man-shift').remove(manShiftId);
    } catch {
      ErrorAlert('Při úpravě přihlášky došlo k chybě');
    }

    setShowFreeUpManShiftModal(false);
  };

  return (
    <>
      <div className="p-2">
        <SecondaryButton onClick={startCancelFlow}>
          Zrušit přihlášku na směnu
        </SecondaryButton>
      </div>
      <AlertModal
        title="Vrátit Tymberovi život?"
        message="Tymber má potvrzenou směnu a do jejího začátku zbývá méně než 48 hodin. Chcete mu za zrušení vrátit 1 život?"
        showModal={showLifeRefundModal}
        yesButtonText="Ano, vrátit"
        noButtonText="Ne, jen zrušit"
        showCancel={false}
        onClose={() => setShowLifeRefundModal(false)}
        onYes={() => onLifeRefundChoice(true)}
        onNo={() => onLifeRefundChoice(false)}
      />
      <AlertModal
        title="Nahradit směny"
        message="Chcete nahradit uvolněná místa volnou kapacitou?"
        showModal={showFreeUpManShiftModal}
        yesButtonText="Nahradit"
        noButtonText="Zavřít bez nahrazení"
        showCancel={false}
        onClose={() => setShowFreeUpManShiftModal(false)}
        onYes={cancelApplication}
        onNo={cancelApplicationAndRemoveManshift}
      />
    </>
  );
};

export default EditApplicationState;
